// Pan and zoom for the world container. Modes decide when a drag pans;
// the wheel always zooms around the cursor.
import type { Application, Container } from 'pixi.js';
import { MAP_SIZE } from '@thrash/sim';
import { toScreen, TILE_W, TILE_H } from './iso.ts';

export class Camera {
  private panStart: { x: number; y: number; wx: number; wy: number } | null = null;

  constructor(private readonly app: Application, readonly world: Container) {
    app.canvas.addEventListener('wheel', e => {
      e.preventDefault();
      this.zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * 0.0015));
    }, { passive: false });
  }

  fit(): void {
    const [cx, cy] = toScreen(MAP_SIZE / 2, MAP_SIZE / 2);
    // the board fills the window: 40 percent bigger than fitting the whole map with margins
    let scale = 1.4 * Math.min(
      this.app.screen.width / (MAP_SIZE * TILE_W * 1.05),
      this.app.screen.height / (MAP_SIZE * TILE_H * 1.25),
    );
    // a portrait phone would show the whole map as a sliver: start on the middle of it instead
    if (this.app.screen.width < 700 && this.app.screen.height > this.app.screen.width) {
      scale = this.app.screen.width / (MAP_SIZE * TILE_W * 0.6);
    }
    this.world.scale.set(scale);
    this.world.position.set(this.app.screen.width / 2 - cx * scale, this.app.screen.height / 2 - cy * scale + 20);
  }

  zoomAt(sx: number, sy: number, factor: number): void {
    const w = this.world;
    const next = Math.min(6, Math.max(0.3, w.scale.x * factor));
    const k = next / w.scale.x;
    w.position.set(sx - (sx - w.position.x) * k, sy - (sy - w.position.y) * k);
    w.scale.set(next);
  }

  beginPan(sx: number, sy: number): void {
    this.panStart = { x: sx, y: sy, wx: this.world.position.x, wy: this.world.position.y };
  }

  movePan(sx: number, sy: number): void {
    if (!this.panStart) return;
    this.world.position.set(this.panStart.wx + sx - this.panStart.x, this.panStart.wy + sy - this.panStart.y);
  }

  panBy(dx: number, dy: number): void {
    this.world.position.set(this.world.position.x + dx, this.world.position.y + dy);
  }

  endPan(): void {
    this.panStart = null;
  }

  /** The canvas the game is drawn and clicked on. */
  get canvas(): HTMLCanvasElement {
    return this.app.canvas;
  }

  get panning(): boolean {
    return this.panStart !== null;
  }
}
